// Messaging Service
// Following existing patterns from ProfileService and ActivityService

import {
  Conversation,
  Message,
  Attachment,
  Notification,
  User
} from '../../types/messaging';
import { signalsService } from '../SocialFeed/signalsService';
import { logger } from '../../utils/logger';
import { config } from '../../config/env';
import { authInterceptor } from '../Authentication/authInterceptor';
import { supabase } from '../../lib/supabase';
import type { RealtimeChannel } from '@supabase/supabase-js';

const API_BASE = `${config.api.baseUrl}/messaging`;

function toUser(row: any): User {
  return {
    id: row.id,
    name: [row.first_name, row.last_name].filter(Boolean).join(' ') || row.first_name || 'Unknown',
    email: row.email ?? '',
    isPremium: !!row.isPremium,
    subscriptionTier: row.subscriptionTier ?? null,
  };
}

function toMessage(row: any): Message {
  return {
    id: row.id,
    conversationId: row.conversation_id,
    senderId: row.sender_id,
    recipientId: row.receiver_id,
    content: row.content,
    // The server sends locked messages with no content at all; this only
    // records that the emptiness is deliberate rather than a data problem.
    isLocked: row.is_locked === true,
    messageType: row.message_type,
    attachments: (row.attachments ?? []).map((a: any) => ({ id: a.id, type: a.type, url: a.url, filename: a.filename, size: a.size_bytes, mimeType: a.mime_type } as Attachment)),
    isRead: row.is_read,
    isDelivered: true,
    isEdited: row.is_edited ?? false,
    editedAt: row.edited_at ? new Date(row.edited_at) : undefined,
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at),
  };
}

function toNotification(row: any): Notification {
  return {
    id: row.id,
    userId: row.user_id,
    type: row.type,
    title: row.title,
    content: row.content,
    relatedId: row.related_id ?? undefined,
    isRead: row.is_read,
    createdAt: new Date(row.created_at),
  };
}

function toConversation(row: any): Conversation {
  return {
    id: row.id,
    participants: (row.participants ?? []).map(toUser),
    lastMessage: row.last_message ? toMessage(row.last_message) : undefined,
    // Was hardcoded to 0, which is why the Unread tab was always empty and the
    // badge never appeared no matter how many messages were waiting.
    unreadCount: row.unread_count ?? 0,
    isMuted: row.is_muted,
    // Server-reported: whether the VIEWER has blocked the other participant.
    // Hardcoded false before, so the settings sheet always offered "Block" —
    // including to someone who had already blocked them.
    isBlocked: row.is_blocked === true,
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at),
    isBet: row.is_bet,
    // Sending is gated for free members outside a bet, and so is reading:
    // outside a bet the content is withheld until they subscribe.
    canSendMessages: row.canSendMessages,
    replyLockedReason: row.replyLockedReason ?? null,
    contentLocked: row.contentLocked === true,
    contentLockedReason: row.contentLockedReason ?? null,
    betLevel: row.bet_level ?? undefined,
    betMessage: row.bet_message ?? undefined,
  };
}

class MessagingService {
  /** Realtime channels, one per conversation, reused for send and receive. */
  private channels = new Map<string, RealtimeChannel>();

  private getAuthToken(): string {
    return authInterceptor.getAccessToken() || '';
  }

  private headers(json = true): HeadersInit {
    const h: HeadersInit = { Authorization: `Bearer ${this.getAuthToken()}` };
    if (json) h['Content-Type'] = 'application/json';
    return h;
  }

  private async unwrap(response: Response): Promise<any> {
    if (!response.ok) {
      // Surface the backend's machine code (e.g. PREMIUM_REQUIRED) so callers
      // can branch — e.g. open the upgrade modal instead of showing an error.
      let code = '';
      let message = response.statusText;
      try {
        const errBody = await response.json();
        code = errBody?.error || '';
        message = errBody?.message || message;
      } catch { /* non-JSON error body */ }
      const err = new Error(message) as Error & { code?: string; status?: number };
      err.code = code;
      err.status = response.status;
      throw err;
    }
    const body = await response.json();
    return body.data;
  }

  // Conversation Methods
  async getConversations(_userId: string): Promise<Conversation[]> {
    try {
      const response = await fetch(`${API_BASE}/conversations`, { headers: this.headers(false) });
      const data = await this.unwrap(response);
      return (data ?? []).map(toConversation);
    } catch (error) {
      console.error("Error fetching conversations:", error);
      throw error;
    }
  }

  async getConversation(conversationId: string): Promise<Conversation> {
    try {
      const response = await fetch(`${API_BASE}/conversations/${conversationId}`, { headers: this.headers(false) });
      const data = await this.unwrap(response);
      return toConversation(data);
    } catch (error) {
      console.error("Error fetching conversation:", error);
      throw error;
    }
  }

  async createConversation(
    participantIds: string[],
    isBet?: boolean,
    betLevel?: 'low' | 'medium' | 'high',
    betMessage?: string
  ): Promise<Conversation> {
    try {
      const response = await fetch(`${API_BASE}/conversations`, {
        method: 'POST',
        headers: this.headers(),
        body: JSON.stringify({ participantIds, isBet, betLevel, betMessage }),
      });
      const data = await this.unwrap(response);
      return toConversation(data);
    } catch (error) {
      console.error("Error creating conversation:", error);
      throw error;
    }
  }

  // Message Methods
  async getMessages(conversationId: string, limit = 50, offset = 0): Promise<Message[]> {
    try {
      const response = await fetch(
        `${API_BASE}/conversations/${conversationId}/messages?limit=${limit}&offset=${offset}`,
        { headers: this.headers(false) }
      );
      const data = await this.unwrap(response);
      return (data.messages ?? []).map(toMessage);
    } catch (error) {
      console.error("Error fetching messages:", error);
      throw error;
    }
  }

  async sendMessage(message: Omit<Message, "id" | "createdAt" | "updatedAt">): Promise<Message> {
    try {
      const response = await fetch(`${API_BASE}/messages`, {
        method: 'POST',
        headers: this.headers(),
        body: JSON.stringify({
          conversationId: message.conversationId,
          recipientId: message.recipientId,
          content: message.content,
          messageType: message.messageType,
          mediaUrl: message.attachments?.[0]?.url,
        }),
      });
      const data = await this.unwrap(response);
      // Sending a message is the costliest thing a user can do here, which
      // makes it the strongest interest signal the product has.
      if (message.recipientId) {
        signalsService.track({
          signalType: 'message_send',
          targetUserId: message.recipientId,
          surface: 'messaging',
          itemId: message.conversationId,
        });
      }
      // Tell the other side to refetch. No content on the wire.
      this.announceNewMessage(message.conversationId);
      return toMessage(data);
    } catch (error) {
      console.error("Error sending message:", error);
      throw error;
    }
  }

  /**
   * Mark a whole conversation read. Called when a thread is opened, which is
   * what reading actually means — the per-message endpoint below existed but
   * nothing ever called it.
   */
  async markConversationAsRead(conversationId: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE}/conversations/${conversationId}/read`, {
        method: 'PUT',
        headers: this.headers(),
      });
      await this.unwrap(response);
    } catch (error) {
      // Failing to clear a badge is not worth interrupting someone reading.
      console.error('Error marking conversation as read:', error);
    }
  }

  async markAsRead(messageId: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE}/messages/${messageId}/read`, {
        method: 'PUT',
        headers: this.headers(false),
      });
      await this.unwrap(response);
    } catch (error) {
      console.error("Error marking message as read:", error);
      throw error;
    }
  }

  async editMessage(messageId: string, content: string): Promise<Message> {
    try {
      const response = await fetch(`${API_BASE}/messages/${messageId}`, {
        method: 'PUT',
        headers: this.headers(),
        body: JSON.stringify({ content }),
      });
      const data = await this.unwrap(response);
      return toMessage(data);
    } catch (error) {
      console.error("Error editing message:", error);
      throw error;
    }
  }

  /** Remove a message from your own thread only; the other copy is untouched. */
  async hideMessage(messageId: string): Promise<void> {
    const response = await fetch(`${API_BASE}/messages/${messageId}/hide`, {
      method: 'PUT',
      headers: this.headers(),
    });
    await this.unwrap(response);
  }

  async deleteMessage(messageId: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE}/messages/${messageId}`, {
        method: 'DELETE',
        headers: this.headers(false),
      });
      await this.unwrap(response);
    } catch (error) {
      console.error("Error deleting message:", error);
      throw error;
    }
  }

  // Attachment Methods
  /** @deprecated Real file upload to storage isn't wired up yet. Once a file is hosted elsewhere,
   *  attach its URL directly via sendMessage's mediaUrl instead. */
  async uploadAttachment(file: File): Promise<Attachment> {
    throw new Error(`uploadAttachment(${file.name}) requires Supabase Storage integration, which is not yet built.`);
  }

  // Conversation Management
  async muteConversation(conversationId: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE}/conversations/${conversationId}/mute`, {
        method: 'PUT',
        headers: this.headers(),
        body: JSON.stringify({ muted: true }),
      });
      await this.unwrap(response);
    } catch (error) {
      console.error("Error muting conversation:", error);
      throw error;
    }
  }

  async unmuteConversation(conversationId: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE}/conversations/${conversationId}/mute`, {
        method: 'PUT',
        headers: this.headers(),
        body: JSON.stringify({ muted: false }),
      });
      await this.unwrap(response);
    } catch (error) {
      console.error("Error unmuting conversation:", error);
      throw error;
    }
  }

  async blockUser(conversationId: string, userId: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE}/conversations/${conversationId}/block`, {
        method: 'POST',
        headers: this.headers(),
        body: JSON.stringify({ userId }),
      });
      await this.unwrap(response);
    } catch (error) {
      console.error("Error blocking user:", error);
      throw error;
    }
  }

  /** Undo a block made from a conversation. */
  async unblockUser(conversationId: string, userId: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE}/conversations/${conversationId}/block`, {
        method: 'DELETE',
        headers: this.headers(),
        body: JSON.stringify({ userId }),
      });
      await this.unwrap(response);
    } catch (error) {
      console.error("Error unblocking user:", error);
      throw error;
    }
  }

  async deleteConversation(conversationId: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE}/conversations/${conversationId}`, {
        method: 'DELETE',
        headers: this.headers(false),
      });
      await this.unwrap(response);
    } catch (error) {
      console.error("Error deleting conversation:", error);
      throw error;
    }
  }

  // Notifications
  async getNotifications(): Promise<Notification[]> {
    try {
      const response = await fetch(`${API_BASE}/notifications`, { headers: this.headers(false) });
      const data = await this.unwrap(response);
      return (data ?? []).map(toNotification);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      throw error;
    }
  }

  async markNotificationRead(notificationId: string): Promise<Notification> {
    try {
      const response = await fetch(`${API_BASE}/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: this.headers(false),
      });
      const data = await this.unwrap(response);
      return toNotification(data);
    } catch (error) {
      console.error("Error marking notification as read:", error);
      throw error;
    }
  }

  async deleteNotification(notificationId: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE}/notifications/${notificationId}`, {
        method: 'DELETE',
        headers: this.headers(false),
      });
      await this.unwrap(response);
    } catch (error) {
      console.error("Error deleting notification:", error);
      throw error;
    }
  }

  // Real-time Methods (WebSocket)
  /**
   * Live updates for one conversation, over a Supabase Realtime BROADCAST
   * channel.
   *
   * Both of these used to be fabrications. `subscribeToConversation` injected
   * a fake message ("This is a mock real-time message!") on a 10% dice roll
   * every 5 seconds, and `sendTypingIndicator` only wrote to the console — so
   * nothing was ever sent and nothing real was ever received.
   *
   * Broadcast rather than postgres_changes, for two reasons:
   *
   *   1. It needs no table in the supabase_realtime publication. Nothing in
   *      this project's migrations adds one, so a postgres_changes
   *      subscription would silently never fire.
   *   2. IT CARRIES NO MESSAGE CONTENT. A postgres_changes payload is the row
   *      itself, which would hand locked message text straight to a free
   *      member and undo the paywall the server enforces in getMessages. A
   *      broadcast says only "something arrived"; the client then refetches
   *      through the gated endpoint, so the lock still decides what they see.
   *
   * `self: false` means a sender never receives their own events — which is
   * what stops you watching yourself type.
   */
  subscribeToConversation(
    conversationId: string,
    onNewMessage: () => void,
    onTyping?: (userId: string, isTyping: boolean) => void,
  ): () => void {
    const channel = this.getConversationChannel(conversationId);

    channel
      .on('broadcast', { event: 'message' }, () => onNewMessage())
      .on('broadcast', { event: 'typing' }, ({ payload }: any) => {
        if (!payload?.userId) return;
        onTyping?.(payload.userId, payload.isTyping === true);
      });

    return () => {
      this.channels.delete(conversationId);
      supabase.removeChannel(channel);
    };
  }

  /** One channel per conversation, shared by the subscriber and the sender. */
  private getConversationChannel(conversationId: string): RealtimeChannel {
    const existing = this.channels.get(conversationId);
    if (existing) return existing;

    const channel = supabase.channel(`conversation:${conversationId}`, {
      config: { broadcast: { self: false } },
    });
    channel.subscribe();
    this.channels.set(conversationId, channel);
    return channel;
  }

  sendTypingIndicator(conversationId: string, isTyping: boolean, userId?: string): void {
    if (!userId) return;
    this.getConversationChannel(conversationId).send({
      type: 'broadcast',
      event: 'typing',
      payload: { userId, isTyping },
    });
  }

  /** Nudge the other participant to refetch. Deliberately carries no content. */
  private announceNewMessage(conversationId: string): void {
    this.getConversationChannel(conversationId).send({
      type: 'broadcast',
      event: 'message',
      payload: {},
    });
  }
}

export const messagingService = new MessagingService();
